"use client"

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { FileText } from "lucide-react"

interface TranscriptItem {
  role: "agent" | "user"
  message: string
}

interface TranscriptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  callId: string
  transcript: TranscriptItem[]
  isLoading: boolean
}

export function TranscriptDialog({
  open,
  onOpenChange,
  callId,
  transcript,
  isLoading,
}: TranscriptDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-0 max-w-2xl max-h-[80vh]" aria-describedby={undefined}>
        <DialogHeader className="min-w-0 pr-6">
          <DialogTitle className="flex min-w-0 items-start gap-2 text-left">
            <FileText className="w-5 h-5 shrink-0" />
            <span className="min-w-0">
              Call Transcript{" "}
              <span className="mt-1 block text-sm font-normal text-muted-foreground [overflow-wrap:anywhere] sm:mt-0 sm:inline sm:text-lg sm:font-semibold sm:text-foreground">{callId}</span>
            </span>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="min-w-0 h-[60vh] pr-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading transcript...</div>
            </div>
          ) : (
            (() => {
              // Filter out entries without actual message content (tool calls, empty messages)
              const messagesWithContent = transcript.filter(
                (item) => item.message && item.message.trim().length > 0
              )

              if (messagesWithContent.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    No transcript available for this call.
                  </div>
                )
              }

              return (
                <div className="space-y-4">
                  {messagesWithContent.map((item, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        item.role === "agent"
                          ? "bg-blue-50 border-l-4 border-blue-400"
                          : "bg-gray-50 border-l-4 border-gray-400"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded ${
                            item.role === "agent"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {item.role === "agent" ? "AI Agent" : "User"}
                        </span>
                      </div>
                      <div className="text-sm text-gray-900 [overflow-wrap:anywhere]">{item.message}</div>
                    </div>
                  ))}
                </div>
              )
            })()
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
